import React, { useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { Formik } from "formik";
import { Button, Form } from "react-bootstrap";
import useAuth from "../../hooks/useAuth.js";
import NotyfContext from "../../contexts/NotyfContext.js";
import { PageLoaderDots } from '../../components/Elements'
import axios from "axios";

function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const notyf = useContext(NotyfContext);
  const { signIn } = useAuth();
  const redirectInfo = location.state || {};

  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    if (!recoveryEmail) {
      notyf.open({ type: "danger", message: "Insira um e-mail", ripple: true, dismissible: true });
      return;
    }
    setIsSending(true);
    try {
      await axios.post("/api/user/password-recovery", { email: recoveryEmail });
      notyf.open({ type: "success", message: "Um e-mail foi enviado com a sua senha", ripple: true, dismissible: true });
      setCountdown(60);
    } catch (error) {
      notyf.open({ type: "danger", message: error.response?.data?.message || "Ocorreu um erro ao enviar e-mail", ripple: true, dismissible: true });
    } finally {
      setIsSending(false);
    }
  };

  if (recoveryMode) {
    return (
      <Form className="mt-3 p-2" onSubmit={handleRecoverySubmit}>
        <div className="mb-3">
          <Button variant="link" onClick={() => setRecoveryMode(false)} className="text-white text-decoration-underline p-0 border-0" style={{ cursor: 'pointer', zIndex: 10, position: 'relative' }}>
            Voltar
          </Button>
        </div>
        <Form.Group className="mb-3">
          <Form.Label>E-mail para recuperação</Form.Label>
          <Form.Control
            size="lg"
            type="email"
            required
            value={recoveryEmail}
            onChange={(e) => setRecoveryEmail(e.target.value)}
          />
        </Form.Group>
        <div className="text-center mt-4">
          <Button type="submit" variant="light" size="lg" disabled={isSending || countdown > 0}>
            {isSending ? <PageLoaderDots color={'#000'} width="25" padding={0} /> : countdown > 0 ? `Reenviar senha (${countdown}s)` : 'Enviar senha'}
          </Button>
        </div>
      </Form>
    );
  }

  return (
    <Formik
      initialValues={{
        email: "",
        password: "",
        submit: false,
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .email("E-mail inválido")
          .required("Campo obrigatório"),
        password: Yup.string()
          .min("6", "Mínimo de 6 caracteres")
          .required("Campo obrigatório"),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await signIn(values.email, values.password);

          // Check if we have redirect information
          if (redirectInfo.redirectTo) {
            // Construct the URL with the token if available
            const redirectPath = redirectInfo.token
              ? `${redirectInfo.redirectTo}?token=${redirectInfo.token}`
              : redirectInfo.redirectTo;

            navigate(redirectPath);
          } else {
            navigate("/");
          }
        } catch (error) {
          var type = "danger";
          var ripple = true;
          var dismissible = true;
          notyf.open({
            type,
            message: error,
            ripple,
            dismissible,
          });
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <Form className="mt-3 p-2" onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>E-mail</Form.Label>
            <Form.Control
              size="lg"
              type="email"
              name="email"
              value={values.email}
              isInvalid={Boolean(touched.email && errors.email)}
              onBlur={handleBlur}
              onChange={handleChange}
            />
            {!!touched.email && (
              <Form.Control.Feedback className="text-white" type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group>
            <Form.Label>Senha</Form.Label>
            <Form.Control
              size="lg"
              type="password"
              name="password"
              value={values.password}
              isInvalid={Boolean(touched.password && errors.password)}
              onBlur={handleBlur}
              onChange={handleChange}
            />
            {!!touched.password && (
              <Form.Control.Feedback className="text-white" type="invalid">
                {errors.password}
              </Form.Control.Feedback>
            )}
            <div className="text-end mt-2">
              <span role="button" onClick={() => setRecoveryMode(true)} className="text-white text-decoration-underline" style={{ cursor: 'pointer' }}>Esqueci minha senha</span>
            </div>
          </Form.Group>
          <div className="text-center mt-4">
            <Button
              type="submit"
              variant="light"
              size="lg"
              disabled={isSubmitting}
            >
              {!isSubmitting ? 'Entrar' : <PageLoaderDots color={'#000'} width="25" padding={0} />}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default SignIn;
